// Theme-local merchandising. No tracking, customer profile or external service.
const apiKey = '__rdbIndexedAds';
if (window[apiKey]) window[apiKey].scan(document);
else {
  const requests = new Map();
  const roots = new WeakSet();
  const selections = new Map();
  const seed = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  let serial = 0;
  const json = url => {
    if (!requests.has(url)) requests.set(url, fetch(url, {credentials:'same-origin'})
      .then(response => { if (!response.ok) throw Error(`ad_data_${response.status}`); return response.json(); })
      .catch(error => { requests.delete(url); throw error; }));
    return requests.get(url);
  };
  const localized = (path,root) => `${root.replace(/\/$/,'')}${path}`;
  const escape = value => String(value ?? '').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  // Shopify's t filter returns HTML-escaped text, even inside JSON. Decode
  // exactly one entity layer at this boundary; card() still escapes every sink.
  // Only individual entities enter the detached decoder, never markup.
  const entityDecoder = document.createElement('textarea');
  const translationText = value => String(value ?? '').replace(/&(?:#(?:x[\da-f]+|\d+)|[a-z][a-z\d]+);/gi,entity=>{
    entityDecoder.innerHTML=entity;return entityDecoder.value;
  });
  function translationLanguage(language) {
    return {ui:Object.fromEntries(Object.entries(language.ui).map(([key,text])=>[key,translationText(text)])),
      tiles:Object.fromEntries(Object.entries(language.tiles).map(([key,copy])=>[key,{...copy,
        footer:translationText(copy.footer),alt:translationText(copy.alt),upper:copy.upper.map(translationText)}]))};
  }
  const amount = (cents,locale) => new Intl.NumberFormat(locale,{minimumFractionDigits:cents%100?2:0,maximumFractionDigits:2}).format(cents/100);
  function selectionContext(data,registry,engine) {
    // Display translations are copy, not merchandising constraints. Keep current
    // native membership while reading semantic traits from the shared index.
    return engine.contextFromCatalog({...data,
      title:registry.collections.find(collection=>collection.id===data.id)?.title || '',
      products:data.products.map(product=>({...product,traits:registry.products[product.id]?.traits ||
        engine.extractTraits([product.handle,product.productType,...(product.tags || [])].join(' '),registry.taxonomy)}))
    },registry.taxonomy);
  }
  async function concurrent(values,work,limit=4) {
    let next=0;
    await Promise.all(Array.from({length:Math.min(limit,values.length)},async()=>{
      while(next<values.length){ const value=values[next++]; await work(value); }
    }));
  }
  async function contextData(root,initial) {
    const products=[];
    for(let page=1;page<=8;page++) {
      const url=new URL(localized(initial.path,root.dataset.routeRoot),location.origin);
      url.searchParams.set('view','rdb-ad-context');url.searchParams.set('page',String(page));
      // Preview cookies carry the chosen unpublished theme; never redirect to Live.
      const data=await json(url.href);
      if(data.id!==initial.id || !Array.isArray(data.products)) throw Error('ad_context_identity');
      products.push(...data.products);
      if(page>=data.pages) return {...initial,title:data.title,tags:data.tags || [],products};
    }
    throw Error('ad_context_pagination_limit');
  }
  async function nativeProduct(id,registry,root) {
    const handle=registry.products[id]?.handle;
    if(!handle) return null;
    try {
      const data=await json(new URL(localized(`/products/${encodeURIComponent(handle)}.js`,root.dataset.routeRoot),location.origin).href);
      if(`gid://shopify/Product/${data.id}`!==id) return null;
      const available=data.variants.filter(v=>v.available);
      return {id,handle:data.handle,title:data.title,available:available.length>0,
        price:available.length?Math.min(...available.map(v=>v.price)):null,
        image:typeof data.featured_image==='string'?data.featured_image:data.featured_image?.src};
    } catch { return null; }
  }
  function priceContext(tile,registry) {
    if(tile.destination.type==='collection')
      return {id:tile.destination.resource_id,path:tile.destination.path};
    // The Classic Roses page's purchasable campaign is the tagged rose set,
    // not the particular rose count photographed on an advertisement.
    if(tile.destination.path==='/pages/shop-flowers?view=classic-roses') {
      const collection=registry.collections.find(item=>item.handle==='rose-collection');
      if(collection) return {id:collection.id,path:'/collections/rose-collection/roses-campaign-2026'};
    }
    throw Error('ad_price_destination_unmapped');
  }
  async function destinationPrice(tile,registry,root) {
    const data=await contextData(root,priceContext(tile,registry));
    const prices=data.products.filter(product=>product.available && Number.isFinite(product.price))
      .map(product=>product.price);
    if(!prices.length) throw Error('ad_destination_has_no_available_price');
    return Math.min(...prices);
  }
  async function pricedCards(choices,runtime,registry,root,language) {
    const discovery=new Map();
    for(const choice of choices) {
      const tile=registry.tiles.find(item=>item.id===choice.id);
      if(tile.kind==='discovery') discovery.set(tile.destination.path,tile);
    }
    const prices=new Map();
    await concurrent([...discovery],async ([path,tile])=>{
      prices.set(path,await destinationPrice(tile,registry,root));
    });
    return choices.map(choice=>{
      const tile=registry.tiles.find(item=>item.id===choice.id);
      const products=choice.availableProductIds.map(id=>runtime[id]).filter(product=>product?.available);
      const price=tile.kind==='discovery'?prices.get(tile.destination.path):Math.min(...products.map(product=>product.price));
      return card(tile,choice,products,price,root,language);
    });
  }
  function productRows(products,root,ui) {
    return products.map(product=>`<li><a class="rdb-campaign-panel__product" data-product-handle="${escape(product.handle)}" href="${escape(localized('/products/'+encodeURIComponent(product.handle),root.dataset.routeRoot))}">
      <span class="rdb-campaign-panel__thumbnail">${product.image?`<img src="${escape(product.image)}" width="120" height="120" loading="lazy" alt="">`:''}</span>
      <span class="rdb-campaign-panel__details"><span class="rdb-campaign-panel__name">${escape(product.title)}</span>
      <span class="rdb-campaign-panel__price" data-rdb-price="${escape(product.price)}" data-rdb-base-currency="${escape(root.dataset.currency)}" data-rdb-trim-base-decimals><small>${escape(ui.from)}</small><span class="rdb-campaign-panel__currency" data-rdb-price-currency>${escape(root.dataset.currency)}</span><span class="rdb-campaign-panel__amount" data-rdb-price-amount>${escape(amount(product.price,root.dataset.locale))}</span></span>
      <span class="rdb-campaign-panel__action">${escape(ui.view)}</span></span></a></li>`).join('');
  }
  function card(tile,selection,products,price,root,language) {
    const copy=language.tiles[tile.key];const ui=language.ui;
    if(!copy || !products.length || !Number.isFinite(price)) throw Error('ad_missing_native_data');
    const layout=tile.layout;
    const style=Object.entries(layout).filter(([key])=>key!=='font').map(([key,value])=>`--ad-${key}:${value}${['x','y','width'].includes(key)?'%':key==='size'?'cqw':''}`).join(';');
    const related=!tile.dependencies.pictured_product_ids.length;
    const id=`IndexedAd-${++serial}-${tile.id}`;
    const target=tile.kind==='product'?'/products/'+encodeURIComponent(products[0].handle):tile.destination.path;
    const rows=tile.kind==='discovery'?productRows(products,root,ui):'';
    const li=document.createElement('li');li.className='rdb-collection-campaign__item';li.dataset.adId=tile.id;li.dataset.placement=selection.placement;
    li.innerHTML=`<article class="rdb-collection-campaign__tile rdb-indexed-ad" data-ad-font="${escape(layout.font)}" data-ad-index="${tile.source_index}" style="${escape(style)}">
      <a class="rdb-indexed-ad__link" href="${escape(localized(target,root.dataset.routeRoot))}">
        <img class="rdb-indexed-ad__image" src="${escape(copy.image)}" width="1086" height="1448" loading="lazy" decoding="async" alt="${escape(copy.alt)}">
        <h2 class="rdb-indexed-ad__heading${copy.upper.length?'':' visually-hidden'}">${copy.upper.length?copy.upper.map(text=>`<span>${escape(text)}</span>`).join(' '):escape(copy.footer)}</h2>
        <span class="rdb-indexed-ad__price" data-rdb-price="${escape(price)}" data-rdb-base-currency="${escape(root.dataset.currency)}" data-rdb-trim-base-decimals><small>${escape(ui.from)}</small><small data-rdb-price-currency>${escape(root.dataset.currency)}</small><b dir="ltr" data-rdb-price-amount>${escape(amount(price,'en-AE'))}</b></span>
        <span class="rdb-indexed-ad__footer" data-ad-footer>${escape(copy.footer)}</span>
      </a>
      ${tile.kind==='discovery'?`<button class="rdb-indexed-ad__info" type="button" hidden data-campaign-open aria-controls="${id}" aria-haspopup="dialog" aria-expanded="false" aria-label="${escape(ui.info)}"><span aria-hidden="true">i</span></button>
      <dialog class="rdb-campaign-panel" data-campaign-panel id="${id}" aria-labelledby="${id}-Title"><div class="rdb-campaign-panel__sheet" data-campaign-panel-content>
        <header class="rdb-campaign-panel__header"><h3 class="rdb-campaign-panel__title" id="${id}-Title" data-campaign-panel-heading tabindex="-1">${escape(related?ui.options:ui.pictured)}</h3>
        <button class="rdb-campaign-panel__close" type="button" data-campaign-close aria-label="${escape(ui.close)}"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18"/></svg></button></header>
        <ul class="rdb-campaign-panel__products" role="list">${rows}</ul></div></dialog>
        <details class="rdb-collection-campaign__fallback" data-campaign-fallback><summary aria-label="${escape(ui.info)}"><span aria-hidden="true">i</span></summary><ul class="rdb-campaign-panel__products" role="list">${rows}</ul></details>`:''}
      </article>`;
    return li;
  }
  function classifyLabels(root,registry,language) {
    for(const item of root.querySelectorAll('[data-ad-id]')) {
      const tile=registry.tiles.find(t=>t.id===item.dataset.adId);
      if(!tile) continue;
      const spans=item.querySelectorAll('.rdb-indexed-ad__heading>span');
      // A role is explicitly compiled per line, never guessed from Arabic text.
      tile.upper_roles?.[root.dataset.locale.startsWith('ar')?'ar':'en']?.forEach((role,n)=>{if(role==='label')spans[n]?.classList.add('rdb-indexed-ad__eyebrow');});
    }
  }
  async function initialize(root) {
    if(roots.has(root))return;roots.add(root);
    try {
      const initial=JSON.parse(root.querySelector('[data-ad-context]').textContent);
      const language=translationLanguage(JSON.parse(root.querySelector('[data-ad-language]').textContent));
      const [registry,engine,data]=await Promise.all([json(root.dataset.index),import(root.dataset.selector),contextData(root,initial)]);
      if(!root.isConnected)return;
      classifyLabels(root,registry,language);
      if(root.hasAttribute('data-home-ads')) {
        await initializeHome(root,initial,registry,engine,data,language);
        return;
      }
      const context=selectionContext(data,registry,engine);
      const runtime=Object.fromEntries(data.products.map(p=>[p.id,p]));
      const ranked=engine.rankLibrary(registry,context,{fillWithRelatedAds:registry.advertising_fallback});
      const candidateIds=new Set(ranked.filter(r=>r.eligible).flatMap(r=>{
        const tile=registry.tiles.find(t=>t.id===r.id);
        return [...tile.dependencies.pictured_product_ids,...tile.dependencies.related_product_ids];
      }));
      const missing=[...candidateIds].filter(id=>!runtime[id]);
      await concurrent(missing,async id=>{const p=await nativeProduct(id,registry,root);if(p)runtime[id]=p;});
      if(registry.advertising_fallback && engine.rankLibrary(registry,context,{runtimeProducts:runtime}).filter(r=>r.eligible).length<6){
        const fallback=engine.rankLibrary(registry,context,{advertisingFallback:true}).filter(r=>r.eligible);
        const needed=new Set(fallback.flatMap(r=>{const t=registry.tiles.find(t=>t.id===r.id);return [...t.dependencies.pictured_product_ids,...t.dependencies.related_product_ids];}));
        await concurrent([...needed].filter(id=>!runtime[id]),async id=>{const p=await nativeProduct(id,registry,root);if(p)runtime[id]=p;});
      }
      if(!root.isConnected)return;
      let recent=[];
      try{recent=JSON.parse(sessionStorage.getItem('rdb-ad-recent:'+context.key)||'[]');}catch{}
      const cached=selections.get(context.key);
      // The same page context remains stable through sort/filter section swaps.
      const result=engine.selectTiles(registry,context,{pins:registry.pins[context.key]||[],seed:cached?.seed||seed+context.key,
        recent:cached?.recent||recent,runtimeProducts:runtime,fillWithRelatedAds:registry.advertising_fallback});
      selections.set(context.key,{seed:cached?.seed||seed+context.key,recent:cached?.recent||recent,result});
      const cards=await pricedCards(result.selected,runtime,registry,root,language);
      // If customers have already interacted, keep the valid native first render
      // instead of moving the target underneath their pointer/focus.
      if(root.dataset.adInteracted==='true')return;
      window.__rdbCollectionCampaignPanels?.destroyAll(root);
      root.querySelector('[data-collection-campaign-rail]').replaceChildren(...cards);
      root.dataset.adState='ready';root.dataset.adShortfall=String(result.shortfall);
      root.hidden=!cards.length;
      if(!cards.length){const heading=root.closest('[data-catalog-root]')?.querySelector('#CatalogHeading');heading?.classList.remove('visually-hidden');}
      classifyLabels(root,registry,language);
      window.__rdbCollectionCampaignPanels?.initializeAll(root);
      try{sessionStorage.setItem('rdb-ad-recent:'+context.key,JSON.stringify(result.selected.filter(x=>x.placement==='rotating').map(x=>x.id)));}catch{}
      root.dispatchEvent(new CustomEvent('rdb:ads-ready',{bubbles:true,detail:{context:context.key,selected:result.selected.map(x=>x.id),shortfall:result.shortfall}}));
    } catch(error) {
      root.dataset.adState='native-fallback';root.dataset.adError=error.message;
      if(!root.querySelector('[data-ad-id]')) {
        root.hidden=true;
        root.closest('[data-catalog-root]')?.querySelector('#CatalogHeading')?.classList.remove('visually-hidden');
      }
    }
  }
  async function initializeHome(root,initial,registry,engine,data,language) {
    const {selectHomeTiles}=await import(root.dataset.homeSelector);
    if(!root.isConnected)return;
    const runtime=Object.fromEntries(data.products.map(product=>[product.id,product]));
    const key='home-discovery',cached=selections.get(key);
    let recent=[];
    try{recent=JSON.parse(sessionStorage.getItem('rdb-ad-recent:'+key)||'[]');}catch{}
    const excludedIds=initial.excluded || [];
    const selectionSeed=cached?.seed || seed+key;
    const selectionRecent=cached?.recent || recent;
    const result=selectHomeTiles(registry,engine,{runtimeProducts:runtime,
      excludedIds,recent:selectionRecent,seed:selectionSeed});
    // Leave the native first render in place after any pointer/focus action.
    if(root.dataset.adInteracted==='true')return;
    const cards=await pricedCards(result.selected,runtime,registry,root,language);
    window.__rdbCollectionCampaignPanels?.destroyAll(root);
    root.querySelector('[data-collection-campaign-rail]').replaceChildren(...cards);
    root.dataset.adState='ready';root.dataset.adShortfall=String(result.shortfall);
    root.hidden=!cards.length;
    classifyLabels(root,registry,language);
    window.__rdbCollectionCampaignPanels?.initializeAll(root);
    selections.set(key,{seed:selectionSeed,recent:selectionRecent,result});
    try{sessionStorage.setItem('rdb-ad-recent:'+key,JSON.stringify(result.selected.map(choice=>choice.id)));}catch{}
    root.dispatchEvent(new CustomEvent('rdb:ads-ready',{bubbles:true,
      detail:{context:key,selected:result.selected.map(choice=>choice.id),shortfall:result.shortfall}}));
  }
  function scan(scope=document) {
    const found=[...(scope.matches?.('[data-indexed-ads]')?[scope]:[]),...scope.querySelectorAll('[data-indexed-ads]')];
    for(const root of found){
      if(!roots.has(root)){
        root.addEventListener('pointerdown',()=>{root.dataset.adInteracted='true';},{once:true,passive:true});
        root.addEventListener('focusin',()=>{root.dataset.adInteracted='true';},{once:true});
        initialize(root);
      }
    }
  }
  window[apiKey]={scan,inspect:()=>[...selections.entries()].map(([context,value])=>({context,...value.result}))};
  new MutationObserver(records=>{for(const r of records)for(const node of r.addedNodes)if(node.nodeType===1)scan(node);}).observe(document.documentElement,{subtree:true,childList:true});
  document.addEventListener('shopify:section:load',event=>scan(event.target));
  scan(document);
}
